import React, { useEffect, useState } from 'react';
import AppNav from './AppNav';
import { useRipple } from '../hooks/useRipple';
import AdUnit from './AdUnit';
import './styles/clickEffects.css';

const NEWSDATA_API_KEY = "pub_515151c76a014867a98ec1224b7f2fd1";

const NEWS_SOURCES = [
  { source: "Nigerian Agric", color: "#0a2e14", bg: "#dcfce7", query: "agriculture food prices market", country: "ng" },
  { source: "Nigerian Business", color: "#795900", bg: "#fef3c7", query: "food commodity naira market prices", country: "ng" },
  { source: "International", color: "#1d4ed8", bg: "#dbeafe", query: "nigeria agriculture food commodity prices", country: null },
];

const SOURCE_META = [
  { source: "Vanguard",     color: "#dc2626", bg: "#fee2e2" },
  { source: "Punch",        color: "#7c3aed", bg: "#ede9fe" },
  { source: "BusinessDay",  color: "#0369a1", bg: "#e0f2fe" },
  { source: "Guardian",     color: "#0f766e", bg: "#ccfbf1" },
  { source: "Nairametrics", color: "#795900", bg: "#fef3c7" },
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

function extractImage(content) {
  if (!content) return null;
  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/i;
  const match = content.match(imgRegex);
  return match ? match[1] : null;
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ');
}

function relTime(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function isAgRelated(title, desc) {
  const text = (title + ' ' + desc).toLowerCase();
  return text.includes('agric') || text.includes('farm') || text.includes('crop') ||
         text.includes('food') || text.includes('price') || text.includes('market');
}

/* ── Featured (hero) card ── */
function FeaturedCard({ item }) {
  const img = item.thumbnail || extractImage(item.content) || extractImage(item.description);
  const desc = stripHtml(item.description || item.content || '').slice(0, 200);
  const isAg = isAgRelated(item.title, item.description);

  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        background: '#ffffff',
        border: '1px solid rgba(10,46,20,0.12)',
        boxShadow: '8px 8px 0px 0px rgba(10,46,20,0.08)',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, transform 0.2s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '12px 12px 0px 0px rgba(212,160,23,0.2)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '8px 8px 0px 0px rgba(10,46,20,0.08)';
      }}
    >
      {/* Image */}
      <div style={{ position: 'relative', height: 260, background: '#0a2e14', overflow: 'hidden' }}>
        {img ? (
          <img
            src={img}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: 'linear-gradient(135deg, #0a2e14 0%, #1a4d2a 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(212,160,23,0.4)" strokeWidth="1" strokeLinecap="round">
              <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 6v6l4 2"/>
            </svg>
          </div>
        )}
        {/* Gradient overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,24,5,0.85) 0%, transparent 60%)',
        }} />
        {/* Badges */}
        <div style={{
          position: 'absolute', top: 14, left: 14,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            background: item._color || '#0a2e14', color: '#fff',
            padding: '3px 10px', fontFamily: 'Space Grotesk, sans-serif',
          }}>
            {item._source}
          </span>
          {isAg && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              background: '#d4a017', color: '#001805',
              padding: '3px 10px', fontFamily: 'Space Grotesk, sans-serif',
            }}>
              Agriculture
            </span>
          )}
        </div>
        {/* Time bottom-left overlay */}
        <div style={{
          position: 'absolute', bottom: 14, left: 14,
          fontSize: 10, color: 'rgba(255,255,255,0.6)',
          fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600,
        }}>
          {relTime(item.pubDate)}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '20px 24px 24px' }}>
        {/* Thin gold rule */}
        <div style={{ width: 32, height: 3, background: '#d4a017', marginBottom: 14 }} />
        <h3 style={{
          fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
          fontWeight: 800,
          color: '#001805',
          lineHeight: 1.3,
          marginBottom: 10,
          fontFamily: 'Epilogue, sans-serif',
          letterSpacing: '-0.01em',
        }}>
          {stripHtml(item.title)}
        </h3>
        <p style={{
          fontSize: 13, color: '#424841', lineHeight: 1.6,
          marginBottom: 16, fontFamily: 'Manrope, sans-serif',
        }}>
          {desc}{desc.length >= 198 ? '...' : ''}
        </p>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 11, fontWeight: 700, color: '#0a2e14',
          fontFamily: 'Space Grotesk, sans-serif',
          letterSpacing: '0.06em', textTransform: 'uppercase',
          borderBottom: '2px solid #d4a017', paddingBottom: 1,
        }}>
          Read Full Story →
        </span>
      </div>
    </a>
  );
}



/* ── Compact card ── */
function CompactCard({ item }) {
  const img = item.thumbnail || extractImage(item.content) || extractImage(item.description);
  const desc = stripHtml(item.description || item.content || '').slice(0, 100);

  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        textDecoration: 'none',
        color: 'inherit',
        background: '#ffffff',
        border: '1px solid rgba(10,46,20,0.08)',
        boxShadow: '4px 4px 0px 0px rgba(10,46,20,0.05)',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, transform 0.2s',
        alignItems: 'stretch',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '8px 8px 0px 0px rgba(212,160,23,0.15)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '4px 4px 0px 0px rgba(10,46,20,0.05)';
      }}
    >
      {/* Left gold accent bar */}
      <div style={{ width: 3, background: item._color || '#0a2e14', flexShrink: 0 }} />

      <div style={{ flex: 1, padding: '14px 16px' }}>
        {/* Source + time */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 8,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            background: item._color || '#0a2e14', color: '#fff',
            padding: '2px 8px', fontFamily: 'Space Grotesk, sans-serif',
          }}>
            {item._source}
          </span>
          <span style={{
            fontSize: 10, color: '#727970', fontWeight: 600,
            fontFamily: 'Space Grotesk, sans-serif',
          }}>
            {relTime(item.pubDate)}
          </span>
        </div>

        <h4 style={{
          fontSize: 13, fontWeight: 700, color: '#001805',
          lineHeight: 1.4, marginBottom: 5,
          fontFamily: 'Epilogue, sans-serif',
        }}>
          {stripHtml(item.title)}
        </h4>
        <p style={{
          fontSize: 11, color: '#424841', lineHeight: 1.5,
          fontFamily: 'Manrope, sans-serif',
        }}>
          {desc}{desc.length >= 98 ? '...' : ''}
        </p>
      </div>

      {img && (
        <div style={{ width: 88, flexShrink: 0 }}>
          <img
            src={img}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}
    </a>
  );
}

/* ── Filter chips ── */
function FilterChips({ sources, active, onToggle }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
      {['ALL', ...sources.map(s => s.source)].map((label, i) => {
        const meta = sources.find(s => s.source === label);
        const isActive = active === label;
        const color = meta?.color || '#0a2e14';
        return (
          <button
            key={label}
            onClick={() => onToggle(label)}
            style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '5px 14px', cursor: 'pointer',
              border: `1px solid ${isActive ? color : 'rgba(10,46,20,0.2)'}`,
              background: isActive ? color : '#ffffff',
              color: isActive ? (color === '#0a2e14' ? '#d4a017' : '#fff') : '#424841',
              fontFamily: 'Space Grotesk, sans-serif',
              boxShadow: isActive ? '4px 4px 0px 0px rgba(10,46,20,0.1)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {label === 'ALL' ? 'All Sources' : label}
          </button>
        );
      })}
    </div>
  );
}



/* ── Skeleton ── */
function SkeletonFeatured() {
  return (
    <div style={{ border: '1px solid rgba(10,46,20,0.08)', background: '#fff', boxShadow: '8px 8px 0px 0px rgba(10,46,20,0.04)', overflow: 'hidden' }}>
      <div style={{ height: 260, background: 'linear-gradient(90deg,#f6f3f2 25%,#e5e2e1 50%,#f6f3f2 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
      <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ width: 32, height: 3, background: '#e5e2e1' }} />
        <div style={{ height: 14, background: '#e5e2e1' }} />
        <div style={{ height: 14, background: '#e5e2e1', width: '80%' }} />
        <div style={{ height: 11, background: '#f6f3f2', width: '60%', marginTop: 4 }} />
      </div>
    </div>
  );
}

function SkeletonCompact() {
  return (
    <div style={{ display: 'flex', border: '1px solid rgba(10,46,20,0.06)', background: '#fff', overflow: 'hidden', height: 90 }}>
      <div style={{ width: 3, background: '#e5e2e1', flexShrink: 0 }} />
      <div style={{ flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ height: 9, background: '#e5e2e1', width: '30%' }} />
        <div style={{ height: 12, background: '#e5e2e1' }} />
        <div style={{ height: 10, background: '#f6f3f2', width: '70%' }} />
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function NewsSection({ priceMode = 'student', onModeToggle, onNavigate, user = null, onUserChange }) {
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useIsMobile();
  const navH = isMobile ? 60 : 72;
  const [lastFetched, setLastFetched] = useState(null);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [fetchStats, setFetchStats] = useState({ ok: 0, fail: 0 });

  useEffect(() => {
    fetchNews();
    const t = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  async function fetchNews() {
    setLoading(true);
    setError(null);
    const results = await Promise.allSettled(
      NEWS_SOURCES.map(src => {
        const params = new URLSearchParams({
          apikey: NEWSDATA_API_KEY,
          q: src.query,
          language: 'en',
          ...(src.country && { country: src.country }),
        });
        return fetch(`https://newsdata.io/api/1/latest?${params}`, { signal: AbortSignal.timeout(10000) })
          .then(r => r.json())
          .then(data => ({ data, src }));
      })
    );

    const all = [];
    let ok = 0, fail = 0;
    results.forEach(r => {
      if (r.status === 'fulfilled' && r.value.data?.results?.length > 0) {
        ok++;
        const { data, src } = r.value;
        data.results.forEach(item => {
          all.push({
            title: item.title,
            description: item.description || item.content || '',
            link: item.link,
            pubDate: item.pubDate,
            thumbnail: item.image_url || null,
            content: item.content || '',
            _source: item.source_name || src.source,
            _color: src.color,
            _bg: src.bg,
          });
        });
      } else { fail++; }
    });

    setFetchStats({ ok, fail });
    if (all.length === 0) { setError('No articles could be loaded. Check your connection.'); setLoading(false); return; }

    const seen = new Set();
    const deduped = all
      .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate))
      .filter(item => {
        const key = item.title?.trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key); return true;
      });

    const sorted = [
      ...deduped.filter(a => isAgRelated(a.title, a.description)),
      ...deduped.filter(a => !isAgRelated(a.title, a.description)),
    ].slice(0, 16);

    setAllArticles(sorted);
    setLastFetched(new Date());
    setLoading(false);
  }

  function handleFilterToggle(source) {
    setActiveFilter(source === activeFilter ? 'ALL' : source);
  }

  const displayed = activeFilter === 'ALL'
    ? allArticles
    : allArticles.filter(a => a._source === activeFilter);

  const [featured, ...rest] = displayed;

  return (
    <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <AppNav activePage="news" priceMode={priceMode} onModeToggle={onModeToggle} onNavigate={onNavigate} isMobile={isMobile} user={user} onUserChange={onUserChange} />
      <div style={{ height: navH }} />

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '2.5rem 2rem 4rem' }}>

        {/* ── Page Header ── */}
          <header style={{
            marginBottom: '2rem',
            animation: 'fadeUp 0.5s ease both',
            position: 'relative',
            padding: '2rem 2rem 1.5rem',
            margin: '0 -2rem 2rem',
            overflow: 'hidden',
          }}>

          {/* Faint background image */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(https://www.shutterstock.com/image-photo/newspaper-headline-agricultural-advances-260nw-2677888167.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.06,
            filter: 'grayscale(100%)',
            pointerEvents: 'none',
            zIndex: 0,
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* ... all existing header content inside here ... */}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h1 style={{
                fontFamily: 'Epilogue, sans-serif',
                fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                fontWeight: 900,
                color: '#001805',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                marginBottom: 10,
              }}>
                Agriculture &amp; Food<br />Price News
              </h1>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                fontSize: 11, color: '#727970', fontFamily: 'Manrope, sans-serif', flexWrap: 'wrap',
              }}>
                {/* Live dot */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block', boxShadow: '0 0 0 3px rgba(22,163,74,0.2)' }} />
                  Live from Nigerian newspapers
                </span>
                {lastFetched && (
                  <span>· Updated {relTime(lastFetched.toISOString())}</span>
                )}
                {!loading && fetchStats.ok > 0 && (
                  <span style={{ color: '#0a2e14', fontWeight: 700 }}>· {fetchStats.ok} sources active</span>
                )}
              </div>
            </div>

            {/* Refresh button */}
            <button
              onClick={fetchNews}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: '#0a2e14', background: '#ffffff',
                border: '1px solid rgba(10,46,20,0.2)',
                padding: '10px 20px', cursor: 'pointer',
                fontFamily: 'Space Grotesk, sans-serif',
                boxShadow: '4px 4px 0px 0px rgba(10,46,20,0.08)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#0a2e14'; e.currentTarget.style.color = '#d4a017'; e.currentTarget.style.boxShadow = '6px 6px 0px 0px rgba(212,160,23,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#0a2e14'; e.currentTarget.style.boxShadow = '4px 4px 0px 0px rgba(10,46,20,0.08)'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
              </svg>
              Refresh
            </button>
          </div>

          {/* Gold divider */}
          <div style={{ marginTop: '1.5rem', height: 1, background: 'linear-gradient(to right, #d4a017 0%, rgba(212,160,23,0.1) 60%, transparent 100%)' }} />
        </header>

        {/* ── Filter Chips ── */}
        {!loading && allArticles.length > 0 && (
          <FilterChips
            sources={SOURCE_META.filter(s => allArticles.some(a => a._source === s.source))}
            active={activeFilter}
            onToggle={handleFilterToggle}
          />
        )}

        {error && (
          <div style={{
            background: '#fef2f2', borderLeft: '3px solid #dc2626',
            border: '1px solid rgba(220,38,38,0.2)',
            padding: '14px 16px', fontSize: 12, color: '#b91c1c',
            display: 'flex', alignItems: 'flex-start', gap: 10,
            marginBottom: 16, fontFamily: 'Manrope, sans-serif',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Could not load news</div>
              <div>{error}</div>
            </div>
          </div>
        )}

        {/* ── Content Grid ── */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 24 }}>
            <SkeletonFeatured />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1,2,3,4,5].map(i => <SkeletonCompact key={i} />)}
            </div>
          </div>
        ) : displayed.length === 0 ? (
          <div style={{
            background: '#ffffff', border: '1px solid rgba(10,46,20,0.1)',
            boxShadow: '8px 8px 0px 0px rgba(10,46,20,0.04)',
            padding: '3rem 2rem', textAlign: 'center',
            fontSize: 13, color: '#424841', fontFamily: 'Manrope, sans-serif',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c2c8bf" strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 12px', display: 'block' }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {activeFilter !== 'ALL'
              ? `No ${activeFilter} articles found. Try another source or refresh.`
              : 'No articles found. RSS feeds may be temporarily unavailable.'}
          </div>
        ) : (
          <>
            {/* Hero + sidebar layout */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 20,
              marginBottom: 20,
            }}>
              {/* Featured */}
              {featured && <FeaturedCard item={featured} />}

              {/* Right column: up to 4 compact cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {rest.slice(0, 4).map((item, i) => (
                  <CompactCard key={`${item._source}-${i}`} item={item} />
                ))}
              </div>
            </div>

            {/* ── Editorial context strip ── */}
            <div style={{
              margin: '20px 0',
              padding: '14px 18px',
              background: 'linear-gradient(135deg, #0a2e14 0%, #1a4731 100%)',
              borderLeft: '4px solid #d4a017',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, fontFamily: 'Manrope, sans-serif' }}>
                <strong style={{ color: '#d4a017' }}>NaijaAgro Editorial Note:</strong> Our team curates and highlights stories relevant to Nigerian agricultural commodity prices, food security, and market intelligence. Click any headline to read the full article on the original publisher's website.
              </p>
            </div>

            {/* ── Ad between hero and more stories ── */}
            <AdUnit slot="9095850875" format="auto" style={{ margin: '0 0 20px' }} />

            {/* Section label for remaining articles */}
            {rest.length > 4 && (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  marginBottom: 14,
                }}>
                  <div style={{ width: 3, height: 18, background: '#d4a017' }} />
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: '#0a2e14', fontFamily: 'Space Grotesk, sans-serif',
                  }}>
                    More Stories
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(10,46,20,0.1)' }} />
                </div>

                {/* Remaining in 3-col grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: 12,
                }}>
                  {rest.slice(4).map((item, i) => (
                    <CompactCard key={`more-${item._source}-${i}`} item={item} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
        <AdUnit
          slot="8923004216"
          format="auto"
          style={{ marginBottom: 24 }}
        />
        {/* ── Attribution ── */}
        <div style={{
          marginTop: 24,
          padding: '12px 16px',
          background: '#f0eded',
          borderLeft: '3px solid rgba(10,46,20,0.15)',
          fontSize: 10, color: '#727970',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          fontFamily: 'Manrope, sans-serif',
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          News sourced from Vanguard, Punch, BusinessDay, Guardian and Nairametrics. Content belongs to respective publishers. Click any card to read on source site.
        </div>
      </main>
    </div>
  );
}